import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Activity, ArrowRight, Music, Sliders, Mic2, Library, Sparkles } from 'lucide-react'
import { motion } from 'framer-motion'
import FileUpload from '../components/FileUpload'
import { getSystemCapabilities } from '../services/api'
import { useSessionData } from '../hooks/useSessionData'
import { useAppStore } from '../stores/appStore'

const actions = [
  { label: 'Stem Separation', copy: 'Extract drums, bass, vocals, and more.', path: '/stems', icon: Music },
  { label: 'Mastering', copy: 'Preset or reference-based mastering chain.', path: '/mastering', icon: Sliders },
  { label: 'Analysis', copy: 'Key, BPM, loudness, and spectral metrics.', path: '/analysis', icon: Activity },
  { label: 'Voice Conversion', copy: 'Model-based conversion with pitch controls.', path: '/voice', icon: Mic2 },
] as const

export default function HomePage() {
  const navigate = useNavigate()
  const { isLoading, error, refresh } = useSessionData()
  const { files, jobs, setSelectedInputFileId } = useAppStore()
  const [runtimeWarnings, setRuntimeWarnings] = useState<string[]>([])

  const activeJobs = useMemo(
    () => jobs.filter((job) => job.status === 'pending' || job.status === 'processing'),
    [jobs]
  )
  const recentFiles = useMemo(() => files.slice(0, 4), [files])

  useEffect(() => {
    getSystemCapabilities()
      .then((response) => {
        const warnings = response.capabilities
          .filter((capability) => !capability.available)
          .map((capability) => `${capability.name} unavailable`)
        setRuntimeWarnings(warnings)
      })
      .catch(() => setRuntimeWarnings([]))
  }, [])

  return (
    <div className="space-y-8 pb-16">
      <section className="hero-grid">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="studio-panel hero-panel"
        >
          <div className="studio-chip w-fit mb-4">
            <Sparkles className="w-3.5 h-3.5" />
            Dark Studio Session
          </div>
          <h1 className="hero-title">Cloud-ready AI audio production in one workspace.</h1>
          <p className="hero-copy">
            Upload once, process across stems/mastering/analysis/voice, and keep every output tied
            to your anonymous session.
          </p>
          <div className="flex flex-wrap items-center gap-3 mt-6">
            <button type="button" className="studio-btn" onClick={() => navigate('/stems')}>
              Start with stems
              <ArrowRight className="w-4 h-4" />
            </button>
            <button type="button" className="studio-btn-secondary" onClick={refresh}>
              Refresh session data
            </button>
          </div>
          {error ? <p className="text-sm text-rose-300 mt-4">{error}</p> : null}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.05 }}
          className="studio-panel"
        >
          <h2 className="panel-title mb-2">Upload Source Audio</h2>
          <p className="panel-copy mb-4">This upload is immediately available in every tool page.</p>
          <FileUpload
            role="input"
            onUploadComplete={(uploaded) => {
              if (uploaded[0]) {
                setSelectedInputFileId(uploaded[0].id)
              }
            }}
          />
          <div className="stats-grid mt-5">
            <div>
              <p className="stats-label">Session Files</p>
              <p className="stats-value">{files.length}</p>
            </div>
            <div>
              <p className="stats-label">Active Jobs</p>
              <p className="stats-value">{activeJobs.length}</p>
            </div>
            <div>
              <p className="stats-label">Completed Jobs</p>
              <p className="stats-value">{jobs.filter((job) => job.status === 'completed').length}</p>
            </div>
          </div>
        </motion.div>
      </section>

      {runtimeWarnings.length > 0 ? (
        <section className="studio-panel">
          <h2 className="panel-title mb-2">Runtime Notes</h2>
          <p className="panel-copy mb-3">
            Some advanced model paths are unavailable in the current backend runtime.
          </p>
          <div className="space-y-1">
            {runtimeWarnings.slice(0, 4).map((warning) => (
              <p key={warning} className="text-sm text-amber-300">
                {warning}
              </p>
            ))}
          </div>
        </section>
      ) : null}

      <section className="grid md:grid-cols-2 gap-4">
        {actions.map((action, index) => {
          const Icon = action.icon
          return (
            <motion.button
              key={action.path}
              type="button"
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: 0.08 + index * 0.04 }}
              onClick={() => navigate(action.path)}
              className="action-card text-left"
            >
              <div className="action-icon">
                <Icon className="w-5 h-5" />
              </div>
              <h3 className="text-lg font-semibold mt-4">{action.label}</h3>
              <p className="text-sm text-zinc-300 mt-2">{action.copy}</p>
              <span className="text-xs text-brand-mint mt-4 inline-flex items-center gap-1">
                Open workspace <ArrowRight className="w-3.5 h-3.5" />
              </span>
            </motion.button>
          )
        })}
      </section>

      <section className="grid xl:grid-cols-2 gap-4">
        <div className="studio-panel">
          <h3 className="panel-title mb-3">Recent Files</h3>
          {isLoading ? <p className="panel-copy">Loading session files...</p> : null}
          {!isLoading && recentFiles.length === 0 ? (
            <div className="empty-block">
              <Library className="w-4 h-4" />
              <span>No files yet. Upload to get started.</span>
            </div>
          ) : null}
          <div className="space-y-2">
            {recentFiles.map((file) => (
              <button
                key={file.id}
                type="button"
                className="asset-row"
                onClick={() => {
                  setSelectedInputFileId(file.id)
                  navigate('/stems')
                }}
              >
                <div className="min-w-0">
                  <p className="truncate font-medium">{file.original_filename}</p>
                  <p className="text-xs text-zinc-400">{file.role} • {file.format.toUpperCase()}</p>
                </div>
                <ArrowRight className="w-4 h-4 text-zinc-400" />
              </button>
            ))}
          </div>
        </div>

        <div className="studio-panel">
          <h3 className="panel-title mb-3">Live Jobs</h3>
          {activeJobs.length === 0 ? (
            <div className="empty-block">
              <Activity className="w-4 h-4" />
              <span>No active jobs in this session.</span>
            </div>
          ) : (
            <div className="space-y-2">
              {activeJobs.slice(0, 6).map((job) => (
                <button
                  key={job.id}
                  type="button"
                  className="asset-row"
                  onClick={() => navigate('/history')}
                >
                  <div>
                    <p className="font-medium capitalize">{job.job_type.replace('_', ' ')}</p>
                    <p className="text-xs text-zinc-400">{job.current_step || 'Running...'}</p>
                  </div>
                  <span className="text-sm text-brand-mint">{Math.round(job.progress)}%</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  )
}
